import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType } from '@/types/shared';

/** Actions that a Weavy paywall call to action can request. */
export type WeavyPaywallAction =
  | {
      type: 'checkout_plan';
      label: string;
    }
  | {
      type: 'open_plan_comparison';
      label: string;
    };

/** Actions emitted by the paywall modal, including UI dismissal. */
export type WeavyPaywallModalAction = WeavyPaywallAction | { type: 'close' };

export type WeavyPaywallPlanTier = SubscriptionType.Free | SubscriptionType.Starter | SubscriptionType.Pro;

/** Configuration required to render a Weavy paywall for an entrypoint. */
export interface WeavyPaywallEntrypointConfig {
  internalDescription: string;
  planTier: WeavyPaywallPlanTier;
  targetPlanTier: WeavyPaywallPlanTier;
  copy: {
    title: string;
    description: string;
    /** Interpolation values for `description`; the modal applies them when it translates. */
    descriptionValues: Record<string, string>;
  };
  /** Feature the paywall is selling, listed first on the target plan card. */
  highlightedFeature?: string;
  primaryAction: WeavyPaywallAction;
  secondaryAction: WeavyPaywallAction;
}

/** Weavy-owned paywall entrypoints, kept separate from the Figma Web entrypoint registry. */
// TODO (eliu): Update copy
export const WEAVY_PAYWALL_ENTRYPOINTS = {
  SHARE_BUTTON: {
    internalDescription: 'Paywall triggered when a Free plan user attempts to share a weave file.',
    planTier: SubscriptionType.Free,
    targetPlanTier: SubscriptionType.Starter,
    copy: {
      title: I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.TITLE,
      description: I18N_KEYS.PAYWALLS.FREE_PLAN_PAYWALL.DESCRIPTION,
      descriptionValues: { targetPlan: 'Starter', features: 'file sharing, full access to all AI models' },
    },
    primaryAction: {
      type: 'checkout_plan',
      label: I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA,
    },
    secondaryAction: {
      type: 'open_plan_comparison',
      label: I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.SEE_ALL_PLANS,
    },
  },
  VOICE_INPUT_FREE: {
    internalDescription: 'Paywall triggered when a Free plan user clicks the locked voice input mic in a prompt node.',
    planTier: SubscriptionType.Free,
    targetPlanTier: SubscriptionType.Pro,
    copy: {
      title: I18N_KEYS.PAYWALLS.VOICE_INPUT_PAYWALL.TITLE,
      description: I18N_KEYS.PAYWALLS.VOICE_INPUT_PAYWALL.DESCRIPTION,
      descriptionValues: { currentPlan: 'Free', targetPlan: 'Pro' },
    },
    highlightedFeature: I18N_KEYS.PAYWALLS.VOICE_INPUT_PAYWALL.FEATURES.VOICE_DICTATION,
    primaryAction: {
      type: 'checkout_plan',
      label: I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA,
    },
    secondaryAction: {
      type: 'open_plan_comparison',
      label: I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.SEE_ALL_PLANS,
    },
  },
  VOICE_INPUT_STARTER: {
    internalDescription:
      'Paywall triggered when a Starter plan user clicks the locked voice input mic in a prompt node.',
    planTier: SubscriptionType.Starter,
    targetPlanTier: SubscriptionType.Pro,
    copy: {
      title: I18N_KEYS.PAYWALLS.VOICE_INPUT_PAYWALL.TITLE,
      description: I18N_KEYS.PAYWALLS.VOICE_INPUT_PAYWALL.DESCRIPTION,
      descriptionValues: { currentPlan: 'Starter', targetPlan: 'Pro' },
    },
    highlightedFeature: I18N_KEYS.PAYWALLS.VOICE_INPUT_PAYWALL.FEATURES.VOICE_DICTATION,
    primaryAction: {
      type: 'checkout_plan',
      label: I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA,
    },
    secondaryAction: {
      type: 'open_plan_comparison',
      label: I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.SEE_ALL_PLANS,
    },
  },
  VIDEO_MODEL: {
    internalDescription: 'Paywall triggered when a Free plan user attempts to run a video model.',
    planTier: SubscriptionType.Free,
    targetPlanTier: SubscriptionType.Starter,
    copy: {
      title: I18N_KEYS.PAYWALLS.VIDEO_MODEL_PAYWALL.TITLE,
      description: I18N_KEYS.PAYWALLS.FREE_PLAN_PAYWALL.DESCRIPTION,
      descriptionValues: { targetPlan: 'Starter', features: 'video generation, unlimited workflows' },
    },
    primaryAction: {
      type: 'checkout_plan',
      label: I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA,
    },
    secondaryAction: {
      type: 'open_plan_comparison',
      label: I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.SEE_ALL_PLANS,
    },
  },
  CUSTOM_FONTS: {
    internalDescription:
      'Paywall triggered when a Free plan workspace admin clicks Upgrade in the compositor font dropdown.',
    planTier: SubscriptionType.Free,
    targetPlanTier: SubscriptionType.Starter,
    copy: {
      title: I18N_KEYS.PAYWALLS.CUSTOM_FONTS_PAYWALL.TITLE,
      description: I18N_KEYS.PAYWALLS.CUSTOM_FONTS_PAYWALL.DESCRIPTION,
      descriptionValues: { currentPlan: 'Free', targetPlan: 'Starter' },
    },
    highlightedFeature: I18N_KEYS.PAYWALLS.CUSTOM_FONTS_PAYWALL.FEATURES.CUSTOM_FONTS,
    primaryAction: {
      type: 'checkout_plan',
      label: I18N_KEYS.PAYMENTS.PRICING_TABLE.PAID_PLAN_CTA,
    },
    secondaryAction: {
      type: 'open_plan_comparison',
      label: I18N_KEYS.SHARE_WORKFLOW_MODAL.FREE_PLAN_PAYWALL.SEE_ALL_PLANS,
    },
  },
} as const satisfies Record<string, WeavyPaywallEntrypointConfig>;

export type WeavyPaywallEntrypointKey = keyof typeof WEAVY_PAYWALL_ENTRYPOINTS;
